import { H2 } from '@/components'
import { Button } from '@/components_deprecated'
import type {
  EditVehicleDocumentPromptResolve,
  VehicleDocumentsQuery,
  VehicleDocumentsQueryVariables,
} from '@/modules/vehicles'
import { VEHICLE_DOCUMENTS_QUERY } from '@/modules/vehicles'
import { useVehicleDocumentHandlers } from '@/modules/vehicles/documents/use-vehicle-document-handlers'
import { VehicleDocumentsTable } from '@/modules/vehicles/documents/vehicle-documents-table'
import { EditVehicleDocumentModal } from './EditVehicleDocumentModal'
import { getExpirationAndNotificationTime } from '@/modules/vehicles/list-filters'
import { useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useMemo } from 'react'
import { useVehicleDocumentMutations } from '../mutations.hooks'

interface VehicleDocumentsProps {
  vehicleId: string
}

export const VehicleDocuments: React.FC<VehicleDocumentsProps> = ({
  vehicleId,
}) => {
  const translations = useTranslate({
    documents: 'common.documents',
    add: 'documents.add-document',
  })

  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()

  const { hasPermissions } = usePermissions()
  const canAdd = hasPermissions(PERMISSIONS.vehicles.add.vehicledocument, true)

  const { data, loading } = useQuery<
    VehicleDocumentsQuery,
    VehicleDocumentsQueryVariables
  >(VEHICLE_DOCUMENTS_QUERY, {
    variables: {
      vehicle: vehicleId,
    },
    onError: onErrorAuto(),
  })

  const documents = useMemo(() => {
    return data?.allVehicleDocuments?.edges?.map(edge => edge?.node) || []
  }, [data])

  const { createDocument } = useVehicleDocumentMutations()
  const { editDocument, removeDocument } = useVehicleDocumentHandlers()

  async function handleCreate() {
    const { data } = await addPrompt<EditVehicleDocumentPromptResolve | null>(
      resolve => <EditVehicleDocumentModal onSubmit={resolve} />
    )
    if (!data) return

    const { expirationDate, notificationTime } =
      getExpirationAndNotificationTime(data.form, data.hasNotification)

    if (!data.form.file) return

    await createDocument({
      variables: {
        input: {
          ...data.form,
          vehicle: vehicleId,
          expirationDate,
          notificationTime,
        },
      },
    })
  }

  return (
    <div className="mt-4">
      <div className="my-4 flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center md:gap-0">
        <H2>{translations.documents}</H2>
        {canAdd && (
          <Button
            iconLeftProps={{ icon: 'plus', size: '1rem', type: 'solid' }}
            height="3rem"
            onClick={handleCreate}
          >
            {translations.add}
          </Button>
        )}
      </div>

      <VehicleDocumentsTable
        documents={documents}
        loading={loading}
        showVehicle={false}
        onEdit={editDocument}
        onDelete={removeDocument}
      />
    </div>
  )
}
